import Button from '$store/components/ui/Button.tsx'
import Icon from '$store/components/ui/Icon.tsx'
import { useComputed, useSignal } from '@preact/signals'
import { useUser } from 'apps/vtex/hooks/useUser.ts'
import { useWishlist } from 'apps/vtex/hooks/useWishlist.ts'
import { assert } from 'site/sdk/assert.ts'
import type { ToAddToWishlist } from '$store/sdk/ga4/events/add_to_wishlist.ts'
import { toAnalytics } from 'site/sdk/ga4/transform/toAnalytics.ts'
import { sendEvent } from 'site/sdk/analytics.ts'
import c from 'site/sdk/c.ts'

export interface Props {
    productID: string
    productGroupID?: string
    analytics?: ToAddToWishlist & { pageType?: string }
    layout?: 'buyModal'
}

function WishlistButton({ productGroupID, productID, analytics, layout }: Props) {
    const { user } = useUser()
    const item = { sku: productID, productId: productGroupID as string }
    const { loading, addItem, removeItem, getItem } = useWishlist()
    const listItem = useComputed(() => getItem(item))
    const fetching = useSignal(false)

    const isUserLoggedIn = Boolean(user.value?.email)
    const inWishlist = Boolean(listItem.value)

    return (
        <Button
            class='gap-2'
            loading={fetching.value}
            aria-label='Add to wishlist'
            onClick={async (e) => {
                e.stopPropagation()
                e.preventDefault()

                if (!isUserLoggedIn) {
                    globalThis.alert(
                        'Por favor, faça login para adicionar produtos à sua lista de desejos.',
                    )

                    return
                }

                if (loading.value) {
                    return
                }

                try {
                    fetching.value = true
                    if (inWishlist) {
                        await removeItem({ id: assert.unreachable(listItem.value).id })
                    } else {
                        if (analytics) {
                            const addToWishlistEvent = toAnalytics({
                                type: 'add_to_wishlist',
                                data: analytics,
                            })
                            sendEvent(addToWishlistEvent)
                        }
                        await addItem(item)
                    }
                } finally {
                    fetching.value = false
                }
            }}
        >
            <span
                class={c(
                    'rounded-full bg-neutral-5 w-[34px] h-[34px] flex items-center justify-center group/wishlist relative',
                    layout === 'buyModal' && 'bg-transparent',
                )}
                data-is-in-wishlist={inWishlist}
            >
                <Icon
                    id='WishlistFill'
                    size={18}
                    class="absolute text-primary-700 transition-opacity group-hover/wishlist:opacity-100 opacity-0 group-data-[is-in-wishlist='true']/wishlist:opacity-100"
                />
                <Icon
                    id='Wishlist'
                    size={18}
                    class="absolute text-[#7C7C7C] transition-opacity group-hover/wishlist:opacity-0 group-data-[is-in-wishlist='true']/wishlist:opacity-0"
                />
            </span>
        </Button>
    )
}

export default WishlistButton
